import { useMemo } from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import { getModuleByPath } from '@core/router/moduleRegistry';

/**
 * BreadcrumbItem represents a single item in the breadcrumb trail
 */
interface BreadcrumbItem {
  label: string;
  path?: string;
  isLast: boolean;
}

/**
 * Route label mappings for breadcrumb display
 * Maps URL segments to translation keys
 */
const routeLabelMap: Record<string, string> = {
  dashboard: 'nav.dashboard',
  fleets: 'modules.fleet.name',
  vozila: 'fleets.vehicles.title',
  servisi: 'fleets.services.title',
  ugovori: 'fleets.contracts.title',
  'nabavka-guma': 'fleets.tireProcurement.title',
  voznje: 'fleets.trips.title', // Vožnje (Trips)
  'putni-nalozi': 'fleets.travelOrders.title', // Putni nalozi (Travel Order Periods)
  izvjestaji: 'fleets.reports.title',
  novi: 'common.new',
  izmeni: 'common.edit',
  zahtevi: 'fleets.requests.title',
  users: 'modules.users.name',
  lista: 'common.list',
  roles: 'users.roles.title',
  permissions: 'users.permissions.title',
  registracije: 'fleets.registrations.title',
  'vozni-park': 'modules.fleet.name',
  dobavljaci: 'fleets.providers.title',
};

/**
 * Breadcrumb Component
 *
 * Displays the current navigation path based on the URL.
 * 
 * Features:
 * - Automatic path parsing
 * - Translated labels
 * - Clickable navigation
 * - ID handling (shows ID in path but doesn't navigate)
 */
export const Breadcrumb = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      return [];
    }

    const items: BreadcrumbItem[] = [];

    // Check if we're in a module
    // const currentModule = getModuleByPath(location.pathname);

    let currentPath = '';
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      // Check if this segment looks like a UUID or ID
      const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
                   /^\d+$/.test(segment);
      
      if (isId) {
        // For IDs, show a shortened version or generic label
        items.push({
          label: segment.length > 8 ? `${segment.substring(0, 8)}...` : segment,
          path: isLast ? undefined : currentPath,
          isLast,
        });
      } else {
        // Look up the label in our map, fall back to the segment itself
        const labelKey = routeLabelMap[segment.toLowerCase()];
        const label = labelKey ? t(labelKey, segment) : segment;
        
        items.push({
          label,
          path: isLast ? undefined : currentPath,
          isLast,
        });
      }
    });

    return items;
  }, [location.pathname, t]);

  // Don't render breadcrumbs for root/dashboard
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        py: 1.5,
        px: { xs: 2, sm: 3 },
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <Typography variant="body2">{t('nav.dashboard')}</Typography>
        </Link>

        {/* Path segments */}
        {breadcrumbs.slice(1).map((item, index) => {
          if (item.isLast) {
            return (
              <Typography
                key={index}
                variant="body2"
                color="text.primary"
                fontWeight={500}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component={RouterLink}
              to={item.path!}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography variant="body2">{item.label}</Typography>
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};
